import React, { useEffect, useState } from 'react';
import { ProgressBar, Pagination } from 'react-bootstrap';
import './Snipers.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import AddressDisplay from '../addressdisplay/AddressDisplay';
import { FaSquareFull } from "react-icons/fa";
const Square = FaSquareFull as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

interface PriceBoxProps {
  pair: string;
  chain: string;
}

interface SniperTransaction {
  transactionHash: string;
  transactionTimestamp: string;
  blocksAfterCreation: number;
}

interface SniperDataResult {
  walletAddress: string;
  snipedTransactions: SniperTransaction[];
  sellTransactions: SniperTransaction[];
  totalSellTransactions: number;
  totalSnipedTransactions: number;
  totalTokensSniped: number;
  totalSnipedUsd: number;
  totalTokensSold: number;
  totalSoldUsd: number;
  currentBalance: number;
  currentBalanceUsdValue: number;
  realizedProfitPercentage: number;
  realizedProfitUsd: number;
}

interface SniperData {
  blockNumber: number;
  blockTimestamp: string;
  transactionHash: string;
  result: SniperDataResult[];
}

const Snipers: React.FC<PriceBoxProps> = ({ pair, chain }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [sniperData, setSniperData] = useState<SniperData | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 9;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        // Call the Netlify Function instead of the direct API
        const response = await fetch(
          `/.netlify/functions/fetch-snipers?pair=${pair}&chain=${chain}`
        );
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        const data: SniperData = await response.json();
        setSniperData(data);
      } catch (err: any) {
        setError(err.message || 'An error occurred.');
      } finally {
        setLoading(false);
      }
    };
  
    if (pair && chain) {
      fetchData();
    }
  }, [pair, chain]); 

  const formatUSD = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatVolume = (volume: number): string => {
    if (volume === null || volume === undefined) return '-';

    if (Math.abs(volume) >= 1_000_000_000) {
      return (
        (volume / 1_000_000_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'B'
      );
    } else if (Math.abs(volume) >= 1_000_000) {
      return (
        (volume / 1_000_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'M'
      );
    } else if (Math.abs(volume) >= 1_000) {
      return (
        (volume / 1_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'K'
      );
    }
    return volume.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Handle pagination clicks
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div style={{ color: 'red' }}>Error: {error}</div>;
  }

  if (!sniperData || !sniperData.result) {
    return <div className='not-found'>No sniper data available yet... 😥</div>;
  }

  if (sniperData.result.length === 0) {
    return <div className='not-found'>No snipers could be detected for this token... ✅</div>;
  }

  // Pagination logic
  const totalSnipers = sniperData.result.length;
  const totalPages = Math.ceil(totalSnipers / itemsPerPage);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSnipers = sniperData.result.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
      <div className="sniper-outer">
        {currentSnipers.map((item) => (
          <div className="sniper-box" key={item.walletAddress}>
            <div className="sniper-items">
              <div className="sniper-item">Sniper 👀</div>
              <div className="sniper-value">
                <AddressDisplay address={item.walletAddress} />
              </div>
            </div>

            <div className="sniper-items">
              <div className="sniper-item">Sniped After</div>
              <div
                className="sniper-value"
                style={{
                  color:
                    item.snipedTransactions[0].blocksAfterCreation === 0
                      ? "rgb(246, 70, 93)"
                      : "rgb(203, 197, 14)"
                }}
              >
                {item.snipedTransactions[0].blocksAfterCreation} blocks
              </div>
            </div>

            <div className="sniper-items">
              <div className="sniper-item">Sniped Tokens</div>
              <div className="sniper-value">
                {formatVolume(item.totalTokensSniped)}
              </div>
            </div>
            <div className="sniper-items">
              <div className="sniper-item">Sniped USD</div>
              <div className="sniper-value">
                ${formatVolume(item.totalSnipedUsd)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
        <Pagination>
          <Pagination.Prev
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          />
          {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
            <Pagination.Item
              key={page}
              active={page === currentPage}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </Pagination.Item>
          ))}
          <Pagination.Next
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          />
        </Pagination>
      </div>
    </>
  );
};

export default Snipers;
